import React from 'react';
import { Container, Box, Typography, Grid, Chip } from '@mui/material';
import { useTheme } from '../../../ThemeContext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FolderIcon from '@mui/icons-material/Folder';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ShareIcon from '@mui/icons-material/Share';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';

const effectiveDate = '18 August 2026';

const PracticeCard = ({ number, title, points, icon, isDarkMode, cardBg, borderColor, textPrimary, textSecondary }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' }, 
      background: cardBg, 
      borderRadius: '24px', 
      p: { xs: 3, md: 5 }, 
      mb: 4, 
      border: `1px solid ${borderColor}`, 
      boxShadow: isDarkMode ? 'none' : '0 12px 32px rgba(0,0,0,0.02)', 
      alignItems: 'center', 
      gap: 4 
    }}
  >
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: { xs: 'auto', md: 120 } }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: isDarkMode ? 'rgba(7,164,245,0.1)' : '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#07a4f5', fontWeight: 900, fontSize: '1.2rem' }}>
        {number}
      </Box>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 24px rgba(7,164,245,0.4)' }}>
        {icon}
      </Box>
    </Box>
    
    <Box sx={{ flexGrow: 1, width: '100%' }}>
      <Typography variant="h5" fontWeight={800} mb={2} sx={{ color: textPrimary }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ color: textSecondary, paddingLeft: 2.5, margin: 0, '& li': { mb: 1, lineHeight: 1.6 } }}>
        {points.map((p, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: p }} />
        ))}
      </Box>
    </Box>
  </Box>
);

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();

  const bgPrimary = isDarkMode ? '#050a14' : '#f8fafc';
  const cardBg = isDarkMode ? '#101726' : '#ffffff';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  const practices = [
    {
      number: '01',
      title: 'Information We Collect',
      icon: <FolderIcon />,
      points: [
        '<strong>Account & Profile Data:</strong><br/><em>Why:</em> To create and authenticate your account, and allow others to find you.<br/><em>Where:</em> Used in Profile pages, Account Settings, and Search results.',
        '<strong>User-Generated Content:</strong><br/><em>Why:</em> To facilitate communication and showcase your work.<br/><em>Where:</em> Used in Chat messaging, Portfolio galleries, and public Forums.',
        '<strong>Financial Data:</strong><br/><em>Why:</em> To process payments for premium features.<br/><em>Where:</em> Used securely on Checkout screens (via third-party processors like Stripe).',
        '<strong>Usage & Diagnostics:</strong><br/><em>Why:</em> To monitor app health, fix bugs, and improve user experience.<br/><em>Where:</em> Collected automatically in the background across all app screens.'
      ]
    },
    {
      number: '02',
      title: 'App Permissions',
      icon: <SettingsSuggestIcon />,
      points: [
        '<strong>Camera & Photo Library:</strong><br/><em>Why:</em> To allow you to take photos/videos or select existing media for your profile and chat.<br/><em>Where:</em> Used when editing your Profile, uploading to your Portfolio, or attaching media in Chat.',
        '<strong>Microphone:</strong><br/><em>Why:</em> To capture audio for video recordings or voice notes.<br/><em>Where:</em> Used in the Audition submission screen and Chat.',
        '<strong>Location Services:</strong><br/><em>Why:</em> To show geographically relevant opportunities and nearby users.<br/><em>Where:</em> Used in the "Nearby Jobs" and "Local Professionals" discovery screens.',
        '<strong>Storage (Read/Write):</strong><br/><em>Why:</em> To save files to your device and read files you want to upload.<br/><em>Where:</em> Used when downloading scripts/call sheets or picking documents for your profile.',
        '<strong>Push Notifications:</strong><br/><em>Why:</em> To keep you informed of critical updates in real-time.<br/><em>Where:</em> Sent to your device system for new Chat messages, Audition invites, and System alerts.',
        '<strong>Contacts (Address Book):</strong><br/><em>Why:</em> To allow you to easily find and connect with friends already using the app, and to share contact cards within chat messages. Your contacts are not sold or shared with third parties.<br/><em>Where:</em> Used in the "Friend Suggestions" section and the "Select Contact" feature within Chat.'
      ]
    },
    {
      number: '03',
      title: 'How We Use Your Information',
      icon: <GpsFixedIcon />,
      points: [
        'To provide and maintain the Service (accounts, profiles, messaging, marketplace, auditions).',
        'To personalize content and recommend relevant jobs, auditions, or nearby professionals based on your location and profile.',
        'To secure the Service, monitor for fraudulent activity, and enforce our Terms of Service.',
        'To perform analytics, troubleshoot app crashes, and improve overall app stability.'
      ]
    },
    {
      number: '04',
      title: 'Disclosure of Your Information',
      icon: <ShareIcon />,
      points: [
        '<strong>Service Providers:</strong> Shared with trusted third parties for hosting, analytics, communications, and payment processing.',
        '<strong>Public Content:</strong> Information you voluntarily post to public profiles or portfolios is visible to other users and businesses.',
        '<strong>Legal Obligations:</strong> We may disclose data if required by law, court order, or to protect our rights and the safety of users.'
      ]
    }
  ];

  return (
    <Box sx={{ background: bgPrimary, minHeight: '100vh', pb: { xs: 8, md: 12 } }}>
      
      {/* Hero Section */}
      <Box sx={{ position: 'relative', pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 12 }, px: { xs: 2, md: 6 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="flex-start" justifyContent="space-between">
            <Grid item xs={12} md={8} lg={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h1" fontWeight={900} sx={{ color: textPrimary, fontSize: { xs: '3rem', md: '4.5rem' }, lineHeight: 1.1 }}>
                  Privacy <Box component="span" sx={{ color: '#07a4f5' }}>Policy</Box>
                </Typography>
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Chip 
                  icon={<CalendarMonthIcon sx={{ color: '#07a4f5 !important' }} />} 
                  label={`Effective Date: ${effectiveDate}`} 
                  sx={{ background: isDarkMode ? 'rgba(7,164,245,0.1)' : '#e0f2fe', color: '#07a4f5', fontWeight: 800, fontSize: '0.85rem', p: 1, borderRadius: '12px' }} 
                />
              </Box>

              <Typography variant="body1" sx={{ color: textSecondary, fontSize: '1.1rem', mb: 5, lineHeight: 1.8, maxWidth: '100%' }}>
                FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Film-hook", "we", "us", "our") operates the Film-hook mobile
                applications and website (together, the "Service"). This Privacy Policy explains what information we collect,
                the permissions our app requests, why we collect it, how we use and share it, and the choices available to users.
              </Typography>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <Box sx={{ background: cardBg, borderRadius: '24px', p: 4, border: `1px solid ${borderColor}`, boxShadow: isDarkMode ? '0 16px 48px rgba(0,0,0,0.4)' : '0 16px 48px rgba(7,164,245,0.08)' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                      <PersonIcon sx={{ color: '#07a4f5' }} fontSize="small" />
                      <Typography fontWeight={700} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Controller</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: textSecondary, pl: 4 }}>FILM-HOOK MEDIAAPPS PRIVATE LIMITED</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                      <EmailIcon sx={{ color: '#07a4f5' }} fontSize="small" />
                      <Typography fontWeight={700} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Email</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: textSecondary, pl: 4 }}>support@filmhookapps.com</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                      <PhoneIcon sx={{ color: '#07a4f5' }} fontSize="small" />
                      <Typography fontWeight={700} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Phone</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: textSecondary, pl: 4 }}>+91 96297 57656</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                      <LocationOnIcon sx={{ color: '#07a4f5' }} fontSize="small" />
                      <Typography fontWeight={700} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Address</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: textSecondary, pl: 4 }}>2nd Floor, 1A, MAIN ROAD, 3, Mysore Rd, near Indian Oil Petrol Pump, Nayandahalli, Bengaluru, Karnataka 560039</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Privacy Practices Section */}
      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Typography variant="h3" fontWeight={900} sx={{ color: textPrimary, mb: 6 }}>
          App Data & Privacy Practices
        </Typography>
        
        {practices.map((practice, idx) => (
          <PracticeCard 
            key={idx}
            {...practice}
            isDarkMode={isDarkMode}
            cardBg={cardBg}
            borderColor={borderColor}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
          />
        ))}

        {/* Remainder of the detailed privacy policy text */}
        <Box sx={{ background: cardBg, borderRadius: '24px', p: { xs: 3, md: 5 }, mt: 6, border: `1px solid ${borderColor}` }}>
           <Typography variant="h5" fontWeight={800} sx={{ color: textPrimary, mb: 3 }}>
             Additional Privacy Details
           </Typography>
           
           <Box sx={{ color: textSecondary, lineHeight: 1.8 }}>
             <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 3, mb: 1 }}>No Third-Party Tracking (App Tracking Transparency)</Typography>
             <Typography paragraph>
               We respect your privacy and do not track you across third-party apps or websites for targeted advertising purposes, nor do we sell your data to data brokers. Therefore, we do not require the use of the App Tracking Transparency (ATT) framework. Any location data collected is used solely for core app functionality (e.g., finding nearby professionals) and is not linked with third-party data for tracking.
             </Typography>

             <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 3, mb: 1 }}>Cookies and Diagnostics</Typography>
             <Typography paragraph>
               We use standard diagnostic tools (like crash reporting) and tokens to help customize the Service, improve your experience, and understand usage patterns internally. You can typically manage device permissions via your settings.
             </Typography>

             <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 3, mb: 1 }}>Data Retention</Typography>
             <Typography paragraph>
               We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law. When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize it.
             </Typography>

             <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 3, mb: 1 }}>Policy for Children</Typography>
             <Typography paragraph>
               We do not knowingly solicit information from or market to children under the age of 18. If we learn that personal information from users less than 18 years of age has been collected without verified parental consent, we will deactivate the account and take reasonable measures to promptly delete such data from our records.
             </Typography>

             <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 3, mb: 1 }}>Your Privacy Rights</Typography>
             <Typography paragraph>
               Depending on your location, you may have certain rights regarding your personal information, including the right to request access, rectification, erasure, or restriction of processing. You also have the right to withdraw consent for location or camera permissions at any time via your device settings. To exercise any of your data rights, please contact us at support@filmhookapps.com.
             </Typography>
           </Box>
        </Box>
      </Container>

      {/* We're Committed Section */}
      <Container maxWidth="xl">
        <Box sx={{ background: isDarkMode ? '#131b2f' : '#f0f9ff', borderRadius: '32px', p: { xs: 4, md: 6 }, border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(7,164,245,0.2)'}`, position: 'relative', overflow: 'hidden' }}>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h3" fontWeight={900} sx={{ color: textPrimary, mb: 2 }}>
                We're Committed to Your Privacy
              </Typography>
              <Typography variant="body1" sx={{ color: textSecondary, mb: 5, maxWidth: '80%' }}>
                We implement industry-standard security measures to protect your information and give you control over your data.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ background: cardBg, p: 3, borderRadius: '20px', border: `1px solid ${borderColor}`, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(7,164,245,0.1)', color: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <VerifiedUserIcon />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Data Security</Typography>
                      <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.8rem' }}>Industry-standard encryption</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ background: cardBg, p: 3, borderRadius: '20px', border: `1px solid ${borderColor}`, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(7,164,245,0.1)', color: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LockIcon />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Your Control</Typography>
                      <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.8rem' }}>Manage your data anytime</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ background: cardBg, p: 3, borderRadius: '20px', border: `1px solid ${borderColor}`, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(7,164,245,0.1)', color: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <VisibilityIcon />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} sx={{ color: textPrimary, fontSize: '0.9rem' }}>Transparency</Typography>
                      <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.8rem' }}>Clear, honest policies</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Bottom Banner */}
        <Box sx={{ mt: 4, background: isDarkMode ? 'rgba(7,164,245,0.05)' : '#f8fafc', border: `1px solid ${isDarkMode ? 'rgba(7,164,245,0.2)' : '#e2e8f0'}`, borderRadius: '16px', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <InfoIcon sx={{ color: '#07a4f5' }} />
          <Typography variant="body2" sx={{ color: textSecondary }}>
            By using Film-hook, you agree to the collection and use of information in accordance with this Privacy Policy.
          </Typography>
        </Box>

      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
